import fileDownload from 'js-file-download';
import React from 'react';
import { getErrorMessage, logger, showErrorToast } from 'src/utils';

export interface IWorkflowPdfFileResponse {
  data?: Blob;
}

export interface IUseWorkflowPdfDownloadOptions<
  TFile extends IWorkflowPdfFileResponse,
> {
  /** Renders the record's PDF on demand; the bytes are never stored server-side. */
  fetchPdf: (jobId: number, id: string) => Promise<TFile | null>;
  /** Message logged when the render fails. */
  logLabel: string;
}

export interface IWorkflowPdfDownloadRequest {
  jobId: number;
  id: string;
  fileName: string;
}

/**
 * @registry Drives the "Download PDF" row action on the workflow record tables: renders the
 * record PDF through the module's preview endpoint and hands the bytes to the browser as a
 * file download. `downloadingId` is the record currently rendering.
 */
export function useWorkflowPdfDownload<TFile extends IWorkflowPdfFileResponse>({
  fetchPdf,
  logLabel,
}: IUseWorkflowPdfDownloadOptions<TFile>) {
  const [downloadingId, setDownloadingId] = React.useState<string | null>(null);

  const fetchRef = React.useRef(fetchPdf);
  fetchRef.current = fetchPdf;
  const logLabelRef = React.useRef(logLabel);
  logLabelRef.current = logLabel;

  const download = async ({
    jobId,
    id,
    fileName,
  }: IWorkflowPdfDownloadRequest) => {
    setDownloadingId(id);
    try {
      const file = await fetchRef.current(jobId, id);
      if (!file?.data) throw new Error('The PDF render returned no content.');

      fileDownload(file.data, fileName);
    } catch (err: unknown) {
      logger.error(logLabelRef.current, { err });
      showErrorToast(getErrorMessage(err));
    }
    setDownloadingId(null);
  };

  return { download, downloadingId };
}
